import { useResourcesStore } from '../stores/resourcesStore';
import { Action } from 'vince-core/types/roles';
import type { ResourceItem } from 'vince-core/types/common';
import { getTablesList } from 'vince-core/api/tables';
import { loadUsers } from 'vince-core/api/users';
import { loadRoles } from 'vince-core/api/roles';
import { loadAPIClients } from 'vince-core/api/apiClients';
import { fetchAllConnections } from 'vince-core/api/connections';
import { loadEnvironments } from 'vince-core/api/environments';
import { loadVariables } from 'vince-core/api/variables';
import { ResourceType } from 'vince-core/types/common';
import type { VariableResponse } from 'vince-core/types/variables';

export default defineNuxtPlugin(() => {
    const store = useResourcesStore();
    store.registerResource('CUSTOM_TABLE', 'menus.submenus.tableManagement', [Action.WRITE, Action.READ], async () => {
        return await getTablesList().then((tables) => {
            return tables.items.map((it) => {
                return {
                    id: it.tableName,
                    key: it.tableName,
                    permissionValue: it.tableName,
                    permissionLabel: it.tableAlias || it.tableName,
                    label: it.tableAlias,
                    description: it.description,
                    resourceType: 'CUSTOM_TABLE',
                    usable: !it.toBeDeleted
                } as ResourceItem;
            });
        });
    }, 'pi pi-table', [ResourceType.META, ResourceType.DATA]);

    store.registerResource('USER', 'menus.submenus.users', [Action.WRITE, Action.READ], async () => {
        return await loadUsers().then((users) => {
            return users.items.map((it) => {
                return {
                    id: it.userId,
                    key: it.userId,
                    label: it.firstName + ' ' + it.lastName,
                    permissionValue: it.email,
                    permissionLabel: it.email,
                    description: it.email,
                    resourceType: 'USER',
                    usable: true
                } as ResourceItem;
            });
        });
    }, 'pi pi-user', [ResourceType.USER]);

    store.registerResource('ROLE', 'common.roles', [Action.WRITE, Action.READ], async () => {
        return await loadRoles().then((roles) => {
            return roles.items.map((it) => {
                return {
                    id: it.roleId,
                    key: it.roleId,
                    label: it.roleName,
                    permissionValue: it.roleName,
                    permissionLabel: it.roleName,
                    description: it.description,
                    resourceType: 'ROLE',
                    usable: true
                } as ResourceItem;
            });
        });
    }, 'pi pi-users', [ResourceType.ROLE]);

    store.registerResource('API_CLIENT', 'foundation.resource.apiclient', [Action.WRITE, Action.READ], async () => {
        const { items } = await loadAPIClients();
        return items.map(it => ({
            id: it.apiClientId,
            key: it.apiClientName,
            label: it.apiClientName,
            resourceType: 'API_CLIENT',
            usable: true
        }));
    }, undefined, [ResourceType.APICLIENT]);

    store.registerResource('CONNECTION', 'foundation.resource.connection', [Action.WRITE, Action.READ], async () => {
        const { items } = await fetchAllConnections();
        return items.map(it => ({
            id: it.connectionId ?? it.connectionName,
            key: it.connectionName,
            label: it.connectionAlias || it.connectionName,
            resourceType: 'CONNECTION',
            usable: true
        }));
    }, undefined, [ResourceType.CONNECTION]);

    store.registerResource('ENVIRONMENT', 'foundation.resource.environment', [Action.WRITE, Action.READ], async () => {
        const { items } = await loadEnvironments();
        return items.map(it => ({
            id: it.environmentId,
            key: it.environmentName,
            label: it.environmentAlias || it.environmentName,
            resourceType: 'ENVIRONMENT',
            usable: true
        }));
    }, undefined, [ResourceType.ENVIRONMENT]);

    store.registerResource('VARIABLE', 'foundation.resource.variable', [Action.WRITE, Action.READ], async () => {
        const { items } = await loadVariables() as VariableResponse;
        return items.map(it => ({
            id: it.variableId,
            key: it.variableName,
            label: it.variableName,
            resourceType: 'VARIABLE',
            usable: true
        }));
    }, undefined, [ResourceType.VARIABLE]);

});
